import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import { ListadosObraModal } from "./ListadosObraModal";

export function ConfirmDelete({ open, onClose, onConfirm, nombre }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-delete-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trash2 size={24} className="text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">¿Eliminar presupuesto?</h3>
        <p className="text-sm text-gray-500 mb-6">
          <strong>"{nombre}"</strong> será enviado a la papelera (recuperable durante 30 días).
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50" data-testid="cancel-delete-btn">Cancelar</button>
          <button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold transition-colors" data-testid="confirm-delete-btn">Sí, eliminar</button>
        </div>
      </div>
    </div>
  , document.body);
}

// ─── Transition / Reject Modal ───────────────────────────────────────────────
export function TransitionModal({ open, onClose, onConfirm, budget, isReject }) {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  if (!open || !budget) return null;

  const nextLabel = isReject ? "Rechazar" : NEXT_STEP_LABEL[budget.status] || "Avanzar";

  const handle = async () => {
    setLoading(true);
    await onConfirm(comment);
    setComment("");
    setLoading(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isReject ? "bg-red-100" : "bg-blue-100"}`}>
          {isReject ? <Ban size={22} className="text-red-600" /> : <ArrowRight size={22} className="text-[#2E6DA4]" />}
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-1 text-center">{nextLabel}</h3>
        <p className="text-xs text-gray-500 mb-4 text-center">
          {isReject ? `Rechazar "${budget.nombre}"` :
            `Avanzar "${budget.nombre}" a ${isReject ? "rechazado" : WORKFLOW_STEPS[getWfIndex(budget.status) + 1]?.label}`}
        </p>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            {isReject ? "Motivo del rechazo *" : "Comentario (opcional)"}
          </label>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={3}
            placeholder={isReject ? "Motivo del rechazo..." : "Observaciones del cambio de estado..."}
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 resize-none"
          />
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handle} disabled={loading || (isReject && !comment.trim())}
            className={`flex-1 text-white rounded-xl py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2
              ${isReject ? "bg-red-600 hover:bg-red-700" : "bg-[#2E6DA4] hover:bg-[#245A8A]"}`}>
            {loading ? <Loader2 size={15} className="animate-spin" /> : null}
            {loading ? "..." : nextLabel}
          </button>
        </div>
      </div>
    </div>
  , document.body);
}

// ─── IniciarComputoModal ────────────────────────────────────────────────────
// Modal especial para transición recepcion/borrador → computo
// Solicita fecha límite de entrega y crea tarea en módulo de Proyectos
export function IniciarComputoModal({ open, onClose, budget, onConfirm }) {
  const [deadlineComputo, setDeadlineComputo] = useState("");
  const [alcance, setAlcance] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && budget) {
      // Pre-rellenar alcance con la info del presupuesto
      const parts = [];
      if (budget.alcance_incluye?.length > 0) {
        parts.push(...(budget.alcance_incluye || []));
      }
      if (budget.obs_tecnicas) parts.push(budget.obs_tecnicas);
      setAlcance(parts.join("\n") || budget.nombre || "");
      setDeadlineComputo("");
    }
  }, [open, budget]);

  if (!open || !budget) return null;

  const handle = async () => {
    if (!deadlineComputo) { toast.error("La fecha límite de entrega es obligatoria"); return; }
    setLoading(true);
    await onConfirm({ deadlineComputo, alcance });
    setLoading(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="iniciar-computo-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-[#EBF3FB] rounded-t-2xl">
          <div className="w-9 h-9 bg-[#2E6DA4] rounded-xl flex items-center justify-center flex-shrink-0">
            <ListChecks size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#1A4B7A]">Iniciar Cómputo</h3>
            <p className="text-xs text-[#2E6DA4]">Se creará una tarea para el Dpto. de Proyectos</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1.5 rounded-lg hover:bg-white/50 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
            <p className="text-xs text-gray-500 mb-0.5">Presupuesto</p>
            <p className="font-bold text-gray-900 text-sm">{budget.nombre}</p>
            <p className="text-xs text-gray-500 mt-0.5">{budget.number} — {budget.cliente || "Sin cliente"}</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Alcance a elaborar <span className="text-gray-400 font-normal">(descripción para el Dpto. de Proyectos)</span>
            </label>
            <textarea
              value={alcance}
              onChange={e => setAlcance(e.target.value)}
              rows={3}
              placeholder="Describe qué debe elaborar el Dpto. de Proyectos..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 resize-none"
              data-testid="computo-alcance-input"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Fecha Límite de Entrega <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={deadlineComputo}
              onChange={e => setDeadlineComputo(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              data-testid="computo-deadline-input"
            />
            <p className="text-xs text-gray-400 mt-1">Esta fecha aparecerá como alerta en el módulo de Proyectos.</p>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handle}
            disabled={loading || !deadlineComputo}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="iniciar-computo-confirm-btn"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <ListChecks size={15} />}
            {loading ? "Iniciando..." : "Iniciar Cómputo"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── PdfOptionsModal ─────────────────────────────────────────────────────────
const PDF_ADMIN_ROLES = ["Superadministrador", "Administrador"];

export function PdfOptionsModal({ open, onClose, budget }) {
  const { user } = useAuth();
  const isPdfAdmin = PDF_ADMIN_ROLES.includes(user?.role);
  const [confeccionadoPor, setConfeccionadoPor] = useState("");
  const [firmaCliente, setFirmaCliente] = useState("");
  // Jun 2026 — firmantes + "Confeccionado por" son GLOBALES (settings).
  // La firma del cliente sigue siendo per-presupuesto.
  const [firmantes, setFirmantes] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    if (!open || !budget) return;
    let cancelled = false;
    // La firma del cliente es per-budget.
    setFirmaCliente(budget.pdf_firma_cliente || budget.cliente || "");
    // Defaults provisorios mientras carga el global (evita parpadeo).
    setConfeccionadoPor(user?.name || "");
    setFirmantes([
      { nombre: "", cargo: "Ing. Responsable" },
      { nombre: "", cargo: "Revisado / Gerencia Técnica" },
    ]);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/pdf-defaults`, opts);
        if (cancelled || !data) return;
        setConfeccionadoPor(data.pdf_confeccionado_por_default || user?.name || "");
        const def = Array.isArray(data.pdf_firmantes_default) ? data.pdf_firmantes_default : [];
        if (def.length > 0) {
          setFirmantes(def.map(f => ({ nombre: f?.nombre || "", cargo: f?.cargo || "" })));
        }
      } catch (e) {
        console.warn("No se pudieron cargar los firmantes globales del PDF:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [open, budget, user]);

  if (!open || !budget) return null;

  const addFirmante = () => {
    if (firmantes.length >= 5) return;
    setFirmantes(p => [...p, { nombre: "", cargo: "" }]);
  };
  const removeFirmante = (idx) => {
    if (firmantes.length <= 1) return;
    setFirmantes(p => p.filter((_, i) => i !== idx));
  };
  const updateFirmante = (idx, field, value) => {
    setFirmantes(p => p.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const handleDownload = async () => {
    // Limpiamos firmantes vacíos (al menos uno debe tener nombre o cargo).
    const validos = firmantes.filter(f => (f.nombre || "").trim() || (f.cargo || "").trim());
    const validosClean = validos.map(f => ({ nombre: (f.nombre || "").trim(), cargo: (f.cargo || "").trim() }));
    setSavingMeta(true);
    try {
      // Los firmantes + "Confeccionado por" se guardan como default GLOBAL
      // (solo si el usuario es Admin/Superadmin). Silencioso para roles inferiores.
      if (isPdfAdmin) {
        try {
          await axios.put(`${API}/settings/pdf-defaults`, {
            pdf_confeccionado_por_default: confeccionadoPor.trim(),
            pdf_firmantes_default: validosClean,
          }, opts);
        } catch (e) {
          console.warn("No se pudieron guardar los firmantes globales:", e);
        }
      }
      // La firma del cliente sí es per-presupuesto.
      try {
        await axios.put(`${API}/budgets/${budget._id}/pdf-meta`, {
          pdf_firma_cliente: firmaCliente.trim(),
        }, opts);
      } catch (e) {
        console.warn("No se pudo guardar la firma del cliente:", e);
      }
    } finally {
      setSavingMeta(false);
    }

    const params = new URLSearchParams();
    if (confeccionadoPor.trim()) params.set("confeccionado_por", confeccionadoPor.trim());
    if (firmaCliente.trim()) params.set("firma_cliente", firmaCliente.trim());
    if (validosClean.length > 0) params.set("firmantes", JSON.stringify(validosClean));
    const url = `${API}/budgets/${budget._id}/export-pdf?${params.toString()}`;
    window.open(url, "_blank");
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="pdf-options-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-red-600" />
            <h2 className="text-base font-bold text-gray-900">Opciones de PDF</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <p className="text-xs text-gray-500 bg-[#EBF3FB] rounded-lg px-3 py-2">
            Presupuesto <strong>{budget.number}</strong> — {budget.nombre}
          </p>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Confeccionado por:</label>
            <input
              value={confeccionadoPor}
              onChange={e => setConfeccionadoPor(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              placeholder="Nombre del responsable que confecciona"
              data-testid="pdf-confeccionado-por-input"
            />
          </div>

          {/* Lista dinámica de firmantes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-gray-700">Firmantes (Nombre + Cargo)</label>
              {firmantes.length < 5 && (
                <button
                  onClick={addFirmante}
                  className="flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] hover:bg-[#EBF3FB] px-2 py-1 rounded-lg border border-[#2E6DA4]/40"
                  data-testid="pdf-add-firmante-btn"
                >
                  <Plus size={11} /> Agregar firmante
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-500 mb-2">
              Estos firmantes son <strong>globales para toda la organización CEN</strong>: al descargar el PDF quedan como predeterminados para todos los presupuestos.
              {!isPdfAdmin && <span className="block text-amber-600 mt-0.5">Tu rol no puede modificar los firmantes globales (se usarán los actuales).</span>}
            </p>
            <div className="space-y-2">
              {firmantes.map((f, i) => (
                <div key={i} className="flex items-start gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50/60" data-testid={`pdf-firmante-row-${i}`}>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      value={f.nombre}
                      onChange={e => updateFirmante(i, "nombre", e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#2E6DA4]/40"
                      placeholder="Nombre (Ej: Arq. Juan Pérez)"
                      data-testid={`pdf-firmante-nombre-${i}`}
                    />
                    <input
                      value={f.cargo}
                      onChange={e => updateFirmante(i, "cargo", e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#2E6DA4]/40"
                      placeholder="Cargo (Ej: Ing. Responsable)"
                      data-testid={`pdf-firmante-cargo-${i}`}
                    />
                  </div>
                  {firmantes.length > 1 && (
                    <button
                      onClick={() => removeFirmante(i)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      data-testid={`pdf-firmante-remove-${i}`}
                      title="Quitar firmante"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Firma y Sello del Cliente:</label>
            <input
              value={firmaCliente}
              onChange={e => setFirmaCliente(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              placeholder="Nombre del cliente para firma"
              data-testid="pdf-firma-cliente-input"
            />
          </div>
        </div>
        <div className="px-6 pb-6 pt-2 flex gap-3 flex-shrink-0 border-t border-gray-100 bg-white rounded-b-2xl">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handleDownload}
            disabled={savingMeta}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="pdf-download-confirm-btn"
          >
            {savingMeta ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {savingMeta ? "Guardando..." : "Descargar PDF"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Download Dropdown (Pedido video #6: un solo botón "Descargar" con opciones) ────
export function DownloadDropdown({ budget, onPdf }) {
  const [open, setOpen] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showListados, setShowListados] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  return (
    <>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
          data-testid="download-budget-btn"
        >
          <Download size={13} /> Descargar <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 z-40 bg-white border border-gray-200 rounded-xl shadow-xl py-1 min-w-[140px]" data-testid="download-options">
            <button
              onClick={() => { setOpen(false); if (onPdf) onPdf(budget); else setShowPdfModal(true); }}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 w-full"
              data-testid="download-pdf-option"
            >
              <FileText size={13} className="text-red-600" /> PDF
            </button>
            <a
              href={`${API}/budgets/${budget._id}/export-excel`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              data-testid="download-excel-option"
            >
              <FileSpreadsheet size={13} className="text-emerald-600" /> Excel (Portada)
            </a>
            <a
              href={`${API}/budgets/${budget._id}/export-excel-cen`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 border-t border-gray-100"
              data-testid="download-excel-cen-option"
              title="Excel formato CEN (4 hojas: Resumen, Cómputo con fórmulas, Bocas, M.O)"
            >
              <FileSpreadsheet size={13} className="text-blue-700" /> Excel CEN (4 hojas)
            </a>
            {/* Fix O (2026-08-31): punto 4 de la carta — los listados de Materiales y
                Tareas de la obra ya existían en el backend (budgets_listados.py) pero
                no tenían ninguna pantalla. Solo para presupuestos aprobados. */}
            {budget.status === "aprobado" && (
              <button
                onClick={() => { setOpen(false); setShowListados(true); }}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 w-full border-t border-gray-100"
                data-testid="listados-obra-option"
              >
                <ListChecks size={13} className="text-[#2E6DA4]" /> Listados de obra
              </button>
            )}
          </div>
        )}
      </div>
      <PdfOptionsModal open={showPdfModal} onClose={() => setShowPdfModal(false)} budget={budget} />
      {showListados && (
        <ListadosObraModal budgetId={budget._id} budgetNumber={budget.number} onClose={() => setShowListados(false)} />
      )}
    </>
  );
}

// ─── Revision History Popover (Pedido video #9: clic en Rev N despliega revisiones) ──
export function RevisionPopover({ history, currentRev, onClose }) {
  const list = Array.isArray(history) ? [...history].reverse() : [];
  return (
    <div className="absolute top-full left-0 mt-1 z-40 bg-white border border-gray-200 rounded-xl shadow-2xl w-[340px] max-h-[400px] overflow-hidden flex flex-col" data-testid="revision-popover">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#EBF3FB] to-white">
        <div className="flex items-center gap-2">
          <History size={14} className="text-[#2E6DA4]" />
          <p className="text-xs font-bold text-gray-800">Histórico de revisiones</p>
          <span className="px-1.5 py-0.5 rounded-md bg-[#2E6DA4] text-white text-[10px] font-bold">Rev {currentRev}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-md hover:bg-white/50 text-gray-400">
          <X size={13} />
        </button>
      </div>
      <div className="overflow-y-auto flex-1">
        {list.length === 0 ? (
          <div className="text-center py-8 px-4 text-gray-400">
            <History size={26} className="mx-auto mb-2 opacity-50" />
            <p className="text-xs">Aún no hay revisiones registradas</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {list.map((h, i) => {
              const meta = STATUS_META[h.status] || { color: "bg-gray-100 text-gray-600", icon: Clock };
              const Icon = meta.icon;
              return (
                <li key={i} className="px-4 py-2.5 hover:bg-gray-50" data-testid={`revision-entry-${i}`}>
                  <div className="flex items-start gap-2">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${meta.color}`}>
                      <Icon size={11} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-xs font-semibold text-gray-800 truncate">{h.label || h.status}</p>
                        <span className="text-[10px] text-gray-400 font-medium flex-shrink-0">{h.timestamp?.slice(0, 10)}</span>
                      </div>
                      {h.user_name && <p className="text-[10px] text-gray-500 mt-0.5">por {h.user_name}</p>}
                      {h.comment && <p className="text-[11px] text-gray-600 mt-1 italic line-clamp-2">"{h.comment}"</p>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Cliente Filter Dropdown (Pedido video #3: botón Clientes con opciones) ────────
export function ClienteFilterDropdown({ value, onChange, clientes }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  const selected = clientes.find(c => c._id === value);
  const filtered = !search.trim() ? clientes :
    clientes.filter(c =>
      (c.cliente || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.codigo || "").toLowerCase().includes(search.toLowerCase())
    );
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
          selected
            ? "bg-[#2E6DA4] text-white shadow-sm hover:bg-[#245A8A]"
            : "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
        }`}
        data-testid="filter-cliente-btn"
      >
        <Users size={14} />
        <span className="truncate max-w-[200px]">
          {selected ? `${selected.codigo} — ${selected.cliente}` : "CLIENTES"}
        </span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-40 bg-white border border-gray-200 rounded-xl shadow-2xl w-[320px] overflow-hidden" data-testid="filter-cliente-dropdown">
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar cliente..."
                className="w-full border border-gray-200 rounded-lg pl-7 pr-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/30 focus:outline-none"
                autoFocus
                data-testid="filter-cliente-search"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <button
              onClick={() => { onChange(""); setSearch(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-gray-50 border-b border-gray-50 ${!value ? "bg-[#EBF3FB] text-[#2E6DA4]" : "text-gray-600"}`}
              data-testid="cliente-option-all"
            >
              <span className="font-bold">Todos los clientes</span>
            </button>
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">Sin resultados</p>
            ) : filtered.map(c => (
              <button
                key={c._id}
                onClick={() => { onChange(c._id); setSearch(""); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 ${
                  value === c._id ? "bg-[#EBF3FB]" : ""
                }`}
                data-testid={`cliente-option-${c._id}`}
              >
                <span className="px-1.5 py-0.5 rounded bg-[#2E6DA4]/10 text-[#1A4B7A] text-[10px] font-bold">{c.codigo}</span>
                <span className="truncate font-medium text-gray-800">{c.cliente}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Budget Card ──────────────────────────────────────────────────────────────
export function BudgetCard({ budget, onView, onEdit, onDelete, onTransition, onReject, onAttach, onAdicional, onDuplicate, canEdit, canAdvance, onPdf }) {
  const status = STATUS_META[budget.status] || STATUS_META.recepcion;
  const tipo = TIPO_META[budget.tipo] || TIPO_META.obra;
  const StatusIcon = status.icon;
  const itemCount = Number.isFinite(budget.items_count) ? budget.items_count : (budget.items || []).filter(i => !i.is_group).length;
  const isTerminal = ["aprobado", "rechazado"].includes(budget.status);
  const nextLabel = NEXT_STEP_LABEL[budget.status];
  const attachCount = budget.attachments_count ?? (budget.attachments || []).length;
  const rev = Number(budget.revision || 0);
  const initials = budget.elaborator_initials || "—";
  const [showRevPopover, setShowRevPopover] = useState(false);
  const revRef = useRef(null);
  useEffect(() => {
    if (!showRevPopover) return;
    const onClick = (e) => { if (revRef.current && !revRef.current.contains(e.target)) setShowRevPopover(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showRevPopover]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-5" data-testid="budget-card">
      {/* Fix EU (2026-10-01): en el celular el monto va debajo de los datos */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${status.color}`}>
              <StatusIcon size={11} /> {status.label}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tipo.color}`}>{tipo.label}</span>
            {budget.is_adicional && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-fuchsia-100 text-fuchsia-700">ADICIONAL</span>}
            {budget.computo_disponible && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"
                title={budget.computo_completed_at ? `Cómputo recibido el ${budget.computo_completed_at.slice(0, 10)}` : "Cómputo recibido del Dpto. de Proyectos"}
                data-testid="computo-disponible-badge"
              >
                <CheckCircle2 size={11} /> CÓMPUTO DISPONIBLE
              </span>
            )}
            {budget.deadline && <DeadlineBadge deadline={budget.deadline} status={budget.status} />}
          </div>
          <h3 className="font-bold text-gray-900 text-sm truncate mb-1" title={budget.nombre}>{budget.nombre}</h3>
          {/* CEN code line: ID Cliente | N° Presupuesto | Fecha | Rev (clickeable) | Iniciales */}
          <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap font-mono mb-1.5 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
            {budget.cliente_codigo && <span className="bg-[#EBF3FB] text-[#1A4B7A] px-1.5 py-0.5 rounded font-bold">{budget.cliente_codigo}</span>}
            <span className="text-[#1A4B7A] font-bold">{budget.number}</span>
            <span className="text-gray-400">|</span>
            <span>{budget.created_at?.slice(0, 10)}</span>
            <span className="text-gray-400">|</span>
            <div className="relative" ref={revRef}>
              <button
                onClick={() => setShowRevPopover(v => !v)}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold border transition-colors ${
                  showRevPopover
                    ? "bg-[#2E6DA4] text-white border-[#2E6DA4]"
                    : "bg-white text-[#2E6DA4] border-[#2E6DA4]/30 hover:bg-[#EBF3FB]"
                }`}
                data-testid={`rev-badge-${budget._id}`}
                title="Ver histórico de revisiones"
              >
                <History size={10} /> Rev {rev}
              </button>
              {showRevPopover && (
                <RevisionPopover
                  history={budget.history || []}
                  currentRev={rev}
                  onClose={() => setShowRevPopover(false)}
                />
              )}
            </div>
            <span className="text-gray-400">|</span>
            <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-bold">{initials}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
            {budget.cliente && <span className="flex items-center gap-1"><Building2 size={11} />{budget.cliente}</span>}
            {budget.obra && <span className="flex items-center gap-1"><Hammer size={11} />{budget.obra}</span>}
            <span>{itemCount} ítems</span>
            {attachCount > 0 && <span className="flex items-center gap-1 text-emerald-600"><Paperclip size={11} />{attachCount} archivo{attachCount !== 1 ? "s" : ""}</span>}
          </div>
          <div className="mt-2">
            <WorkflowStepper status={budget.status} />
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-bold text-[#2E6DA4]">{fmt(budget.total)}</p>
          {/* Fix FG (2026-10-08): presupuesto cotizado en USD */}
          {budget.moneda === "USD" && Number(budget.tipo_cambio) > 0 && (
            <p className="text-xs font-semibold text-emerald-700" data-testid="budget-card-usd">
              US$ {(Number(budget.total || 0) / Number(budget.tipo_cambio)).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
          <p className="text-xs text-gray-400">IVA: {fmt(budget.iva_total)}</p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 mt-4 flex-wrap">
        <button onClick={() => onView(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E6DA4]/10 hover:bg-[#2E6DA4]/20 text-[#2E6DA4] text-xs font-semibold transition-colors" data-testid="view-budget-btn">
          <Eye size={13} /> Ver
        </button>
        {canEdit && (!isTerminal || budget.status === "aprobado") && (
          <button onClick={() => onEdit(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors" data-testid="edit-budget-btn">
            <Edit3 size={13} /> Editar
          </button>
        )}
        <DownloadDropdown budget={budget} onPdf={onPdf} />
        {canEdit && (
          <button onClick={() => onAttach(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold transition-colors" data-testid="attach-budget-btn">
            <Paperclip size={13} /> Adjuntar{attachCount > 0 && <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-purple-700 text-white text-[10px]">{attachCount}</span>}
          </button>
        )}
        {canEdit && !budget.is_adicional && (
          <button onClick={() => onAdicional(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 text-xs font-semibold transition-colors" data-testid="adicional-budget-btn">
            <FilePlus size={13} /> Adicional
          </button>
        )}
        {canEdit && (
          <button onClick={() => onDuplicate && onDuplicate(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-semibold transition-colors" data-testid="duplicate-budget-btn">
            <Copy size={13} /> Duplicar
          </button>
        )}
        {canAdvance && !isTerminal && nextLabel && (
          budget.computo_disponible && (budget.status === "recepcion" || budget.status === "borrador")
            ? (
              <button onClick={() => onTransition(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors shadow-sm" data-testid="ir-cotizacion-btn">
                <ArrowRight size={13} /> Ir a Cotización
              </button>
            ) : (
              <button onClick={() => onTransition(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E6DA4] hover:bg-[#245A8A] text-white text-xs font-semibold transition-colors" data-testid="advance-btn">
                <ArrowRight size={13} /> {nextLabel}
              </button>
            )
        )}
        {canAdvance && !isTerminal && budget.status !== "recepcion" && budget.status !== "borrador" && (
          <button onClick={() => onReject(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors" data-testid="reject-btn">
            <Ban size={13} /> Rechazar
          </button>
        )}
        {canEdit && (
          <button onClick={() => onDelete(budget)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors ml-auto" data-testid="delete-budget-btn">
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── DuplicateModal (FIX 2) ──────────────────────────────────────────────────
export function DuplicateModal({ open, onClose, budget, clientes = [], onDuplicated }) {
  const [cliente, setCliente] = useState("");
  const [obra, setObra] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && budget) {
      setCliente(budget.cliente || "");
      setObra("");
    }
  }, [open, budget]);

  if (!open || !budget) return null;

  const handleConfirm = async () => {
    if (!cliente.trim()) { toast.error("Indicá el cliente del nuevo presupuesto"); return; }
    const match = (clientes || []).find(
      c => (c.cliente || "").trim().toLowerCase() === cliente.trim().toLowerCase()
    );
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/budgets/${budget._id}/duplicate`, {
        cliente: cliente.trim(),
        obra: obra.trim(),
        cliente_id: match?._id || "",
        cliente_codigo: match?.codigo || "",
      }, opts);
      toast.success(`Presupuesto duplicado: ${data.number}`);
      if (typeof onDuplicated === "function") onDuplicated(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al duplicar el presupuesto");
    } finally { setLoading(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="duplicate-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Copy size={18} className="text-teal-600" />
            <h2 className="text-base font-bold text-gray-900">Duplicar Presupuesto</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500 bg-[#EBF3FB] rounded-lg px-3 py-2">
            Original: <strong>{budget.number}</strong> — {budget.nombre}
          </p>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Cliente del nuevo presupuesto *</label>
            <AutocompleteInput
              value={cliente}
              onChange={setCliente}
              suggestions={(clientes || []).map(c => c.cliente).filter(Boolean)}
              inputClassName="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              placeholder="Seleccioná o escribí el cliente"
              data-testid="duplicate-cliente-input"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Obra</label>
            <input
              value={obra}
              onChange={e => setObra(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              placeholder="Obra (opcional)"
              data-testid="duplicate-obra-input"
            />
          </div>
          <p className="text-[11px] text-gray-600 leading-relaxed bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Se va a crear un nuevo presupuesto con todos los ítems, márgenes, marcas y alcance del original.
            El cliente y la obra serán los que indiques arriba. El nuevo arranca en estado <strong>Recepción</strong> (Rev 0).
          </p>
        </div>
        <div className="px-6 pb-6 pt-2 flex gap-3 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="duplicate-confirm-btn"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Copy size={15} />} {loading ? "Duplicando..." : "Duplicar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Item Row ─────────────────────────────────────────────────────────────────
